/**
 * PublicLeagueLayout — the public league site shell (/l/:slug/*)
 * ─────────────────────────────────────────────────────────────────
 * REDESIGNED in the public pages overhaul P3 (2026-06-09). Modern
 * chrome replacing the v2.5.121 minimal header:
 *
 *   • Deep-navy sticky top bar: logo + league name, full nav (Home /
 *     News / Schedule / Standings / Teams / Stats / Playoffs /
 *     Coaches), instant team-search, accent Register CTA.
 *   • Mobile: nav collapses behind a hamburger; search stays.
 *   • Footer: contact/about blocks (league_content_blocks), social
 *     links, sign-in, Game Day attribution.
 *   • Accent color = manager's `theme_color` content block.
 *
 * Outlet context is a SUPERSET of the old contract ({ league,
 * currentSeason, contentBlocks, upcomingGames, registrationOpen })
 * so any page not yet redesigned keeps working; adds { latestPosts,
 * accent }.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams, Link, NavLink, useNavigate } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import {
  container, themeAccent, accentText, NAVY, MUTED, LINE,
} from './pubUi.jsx';

export default function PublicLeagueLayout() {
  const { slug } = useParams();
  const [league, setLeague] = useState(null);
  const [current, setCurrent] = useState(null);
  const [contentBlocks, setContentBlocks] = useState({});
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  // 2026-09-28 — sponsor strip (editor: League → Public site → Sponsors).
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true); setNotFound(false);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setLeague(data.league || null);
        setCurrent(data.current_season || null);
        setContentBlocks(data.content_blocks || {});
        setUpcomingGames(data.upcoming_games || []);
        setLatestPosts(data.latest_posts || []);
        setRegistrationOpen(!!data.registration_open);
        setSponsors(Array.isArray(data.sponsors) ? data.sponsors : []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else console.error('public league fetch failed:', err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (league?.name) document.title = `${league.name} — Game Day`;
    else if (notFound) document.title = 'League not found — Game Day';
  }, [league?.name, notFound]);

  const accent = themeAccent(contentBlocks);

  if (loading) {
    return <Shell><p style={{ padding: 60, textAlign: 'center', color: MUTED }}>Loading…</p></Shell>;
  }
  if (notFound || !league) {
    return (
      <Shell>
        <div style={{ maxWidth: 600, margin: '90px auto', padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>🏒</div>
          <h1 style={{ fontSize: 30, margin: '10px 0 12px', fontWeight: 800 }}>League not found</h1>
          <p style={{ color: MUTED, lineHeight: 1.6 }}>
            This league either doesn't exist or its public pages are turned off.
            If you're a league member, sign in to access it.
          </p>
          <div style={{ marginTop: 24 }}>
            <a href="/login" style={{
              display: 'inline-block', padding: '11px 20px', borderRadius: 12,
              background: '#0a6cff', color: '#fff', fontWeight: 800, textDecoration: 'none',
            }}>Sign in to Game Day</a>
          </div>
        </div>
      </Shell>
    );
  }

  const navItems = [
    { to: `/l/${slug}`, label: 'Home', end: true },
    { to: `/l/${slug}/news`, label: 'News' },
    { to: `/l/${slug}/schedule`, label: 'Schedule' },
    { to: `/l/${slug}/standings`, label: 'Standings' },
    { to: `/l/${slug}/teams`, label: 'Teams' },
    { to: `/l/${slug}/leaders`, label: 'Stats' },
    { to: `/l/${slug}/playoffs`, label: 'Playoffs' },
    { to: `/l/${slug}/coaches`, label: 'Coaches' },
  ];

  return (
    <Shell>
      {/* 2026-09-28 — demo leagues (Admin → Demo) say so on every page. */}
      {league.is_demo && (
        <div role="note" style={{
          background: '#fef3c7', color: '#78350f', borderBottom: '1px solid #fcd34d',
          fontSize: 13, fontWeight: 600, textAlign: 'center', padding: '7px 12px',
        }}>
          Demo league — every team, player and score on this site is fictional.
        </div>
      )}
      <header style={{
        background: NAVY, color: '#fff',
        position: 'sticky', top: 0, zIndex: 50,
        boxShadow: '0 1px 0 rgba(255,255,255,0.08), 0 4px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ ...container, display: 'flex', alignItems: 'center', gap: 14, height: 64 }}>
          {/* Brand */}
          <Link to={`/l/${slug}`} style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: '#fff', minWidth: 0 }}>
            {league.logo_url ? (
              <img src={league.logo_url} alt="" width="38" height="38"
                   style={{ borderRadius: 10, background: '#fff', objectFit: 'contain', flexShrink: 0 }} />
            ) : (
              <span style={{
                width: 38, height: 38, borderRadius: 10, background: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }}>🏒</span>
            )}
            <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
              {league.name}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="pub-desktop-nav" aria-label="League sections"
               style={{ display: 'flex', gap: 2, marginLeft: 8, flex: 1, minWidth: 0, overflowX: 'auto' }}>
            {navItems.map(item => (
              <TopNavLink key={item.to} to={item.to} end={item.end} accent={accent}>
                {item.label}
              </TopNavLink>
            ))}
          </nav>

          <TeamSearch slug={slug} accent={accent} />

          {registrationOpen && (
            <Link to={`/l/${slug}/register`} className="pub-register-cta" style={{
              padding: '9px 16px', borderRadius: 10, background: accent,
              color: accentText(accent), fontWeight: 800, fontSize: 13,
              textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              Register
            </Link>
          )}

          {/* Mobile hamburger */}
          <button type="button" className="pub-hamburger" aria-label="Menu"
            onClick={() => setMenuOpen(o => !o)}
            style={{
              display: 'none', background: 'none', border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 16,
            }}>
            ☰
          </button>
        </div>

        {/* Mobile menu sheet */}
        {menuOpen && (
          <div style={{ background: '#101a30', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '8px 0 14px' }}>
            <div style={container}>
              {navItems.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end}
                  onClick={() => setMenuOpen(false)}
                  style={({ isActive }) => ({
                    display: 'block', padding: '11px 6px',
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.72)',
                    fontWeight: isActive ? 800 : 600, fontSize: 15,
                    textDecoration: 'none',
                    borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
                    paddingLeft: 12,
                  })}>
                  {item.label}
                </NavLink>
              ))}
              <a href="/login" style={{ display: 'block', padding: '11px 12px', color: 'rgba(255,255,255,0.72)', fontSize: 14, textDecoration: 'none' }}>
                Sign in →
              </a>
            </div>
          </div>
        )}

        {/* Tiny responsive switch without a CSS file: hide desktop nav
            below 880px, show hamburger. Below 480px the header's
            non-shrinking items (fixed-width team search + Register
            pill + hamburger) add up to more than a ~390px phone's
            width alongside the brand, causing the header to overflow
            horizontally — shrink them so everything fits on one row. */}
        <style>{`
          @media (max-width: 880px) {
            .pub-desktop-nav { display: none !important; }
            .pub-hamburger { display: inline-block !important; }
          }
          @media (max-width: 480px) {
            .pub-team-search-input { width: 88px !important; padding: 8px 8px !important; font-size: 12px !important; }
            .pub-register-cta { padding: 8px 10px !important; font-size: 12px !important; }
            /* The results dropdown is normally width:280 anchored
               right:0 to the ~88px search box — on a narrow phone that
               box sits mid-header (not flush to the screen edge), so
               a 280px-wide dropdown runs off the left of the viewport.
               Re-anchor it to the viewport instead of the search box. */
            .pub-search-results { position: fixed !important; top: 64px !important; left: 12px !important; right: 12px !important; width: auto !important; }
          }
        `}</style>
      </header>

      <main style={{ padding: '0 0 70px' }}>
        <Outlet context={{
          league,
          currentSeason: current,
          contentBlocks,
          upcomingGames,
          latestPosts,
          registrationOpen,
          accent,
          sponsors,
        }} />
      </main>

      <SponsorStrip sponsors={sponsors} />
      <Footer slug={slug} league={league} blocks={contentBlocks} accent={accent} />
    </Shell>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────

function Shell({ children }) {
  return (
    <div className="pub-shell" style={{
      background: '#fbfcff',
      color: '#101730',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, 'Helvetica Neue', Arial, sans-serif",
      fontSize: 15, lineHeight: 1.55,
    }}>
      {/* This page tree has no MobileLayout/TabletLayout/CapacitorLayout
          ancestor, so the app's JS-driven --mobile-h variable (set by
          the Visual Viewport API for keyboard-safe app screens) is not
          available here. These are plain mobile-web pages, so 100dvh
          (falls back to 100vh on older browsers) is the right unit —
          it avoids the iOS Safari address-bar-shrink gap that a bare
          100vh would leave under the footer. */}
      <style>{`
        .pub-shell { min-height: 100vh; min-height: 100dvh; }
      `}</style>
      {children}
    </div>
  );
}

function TopNavLink({ to, end, accent, children }) {
  return (
    <NavLink to={to} end={end} style={({ isActive }) => ({
      padding: '8px 12px',
      borderRadius: 8,
      fontSize: 13.5,
      fontWeight: 700,
      letterSpacing: '0.01em',
      color: isActive ? '#fff' : 'rgba(255,255,255,0.68)',
      background: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
      boxShadow: isActive ? `inset 0 -2px 0 ${accent}` : 'none',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    })}>
      {children}
    </NavLink>
  );
}

/**
 * Instant team search — fetches the directory once on first focus,
 * filters client-side, ⏎ or click goes to the team page.
 */
function TeamSearch({ slug, accent }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [teams, setTeams] = useState(null); // null = not loaded yet
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  async function ensureTeams() {
    if (teams !== null) return;
    try {
      const { data } = await publicApi.get(`/leagues/${encodeURIComponent(slug)}/teams`);
      setTeams(data?.teams || []);
    } catch {
      setTeams([]);
    }
  }

  const matches = useMemo(() => {
    if (!q.trim() || !teams) return [];
    const needle = q.trim().toLowerCase();
    return teams.filter(t => t.team_name.toLowerCase().includes(needle)).slice(0, 8);
  }, [q, teams]);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function go(teamId) {
    setOpen(false); setQ('');
    navigate(`/l/${slug}/team/${teamId}`);
  }

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0 }}>
      <input
        type="search"
        className="pub-team-search-input"
        value={q}
        placeholder="Find a team…"
        onFocus={() => { setOpen(true); ensureTeams(); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && matches[0]) go(matches[0].team_id); }}
        style={{
          width: 150, padding: '8px 12px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(255,255,255,0.10)', color: '#fff',
          fontSize: 13, outline: 'none',
        }}
      />
      {open && q.trim() && (
        <div className="pub-search-results" style={{
          position: 'absolute', top: '110%', right: 0, width: 280,
          background: '#fff', color: '#101730',
          border: `1px solid ${LINE}`, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(16,23,48,0.18)', overflow: 'hidden',
        }}>
          {teams === null && <div style={{ padding: 12, fontSize: 13, color: MUTED }}>Loading teams…</div>}
          {teams !== null && matches.length === 0 && (
            <div style={{ padding: 12, fontSize: 13, color: MUTED }}>No team matches "{q.trim()}"</div>
          )}
          {matches.map(t => (
            <button key={t.team_id} type="button" onClick={() => go(t.team_id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 14px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 14, fontWeight: 600,
                borderBottom: `1px solid ${LINE}`,
              }}>
              {t.team_name}
              {t.division_name && (
                <span style={{ color: MUTED, fontWeight: 500, fontSize: 12 }}> · {t.division_name}</span>
              )}
            </button>
          ))}
          {matches.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 11, color: MUTED }}>
              ⏎ opens {matches[0].team_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sponsor strip above the footer (2026-09-28). Logos when uploaded,
 * otherwise the sponsor's name as a chip. Links open in a new tab and
 * are marked sponsored.
 */
function SponsorStrip({ sponsors }) {
  if (!sponsors || sponsors.length === 0) return null;
  return (
    <section aria-label="Sponsors" style={{ background: '#fff', borderTop: `1px solid ${LINE}`, padding: '26px 0 30px' }}>
      <div style={container}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, textAlign: 'center', marginBottom: 16 }}>
          Thanks to our sponsors
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
          {sponsors.map((sp) => {
            const inner = sp.logo_url ? (
              <img src={sp.logo_url} alt={sp.name} title={sp.name}
                   style={{ height: 48, maxWidth: 150, objectFit: 'contain', display: 'block' }} />
            ) : (
              <span style={{
                display: 'inline-block', padding: '10px 16px', borderRadius: 10,
                border: `1px solid ${LINE}`, background: '#f8fafc',
                fontWeight: 800, fontSize: 14, color: '#1f2940',
              }}>{sp.name}</span>
            );
            return sp.external_url ? (
              <a key={sp.id} href={sp.external_url} target="_blank" rel="noopener noreferrer sponsored"
                 style={{ textDecoration: 'none' }}>{inner}</a>
            ) : <div key={sp.id}>{inner}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

function Footer({ slug, league, blocks, accent }) {
  const socials = [
    { key: 'social_facebook', label: 'Facebook' },
    { key: 'social_x', label: 'X' },
    { key: 'social_instagram', label: 'Instagram' },
  ].filter(s => blocks[s.key]);

  return (
    <footer style={{ background: NAVY, color: 'rgba(255,255,255,0.78)', padding: '40px 0 28px', fontSize: 14 }}>
      <div style={{ ...container, display: 'flex', gap: 36, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ maxWidth: 380 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#fff', marginBottom: 8 }}>{league.name}</div>
          {league.description && <p style={{ margin: 0, lineHeight: 1.6 }}>{league.description}</p>}
        </div>
        <div>
          <div style={footHead}>Explore</div>
          {[['News', 'news'], ['Schedule', 'schedule'], ['Standings', 'standings'], ['Teams', 'teams'], ['Playoffs', 'playoffs'], ['Registration', 'register']].map(([label, path]) => (
            <Link key={path} to={`/l/${slug}/${path}`} style={footLink}>{label}</Link>
          ))}
        </div>
        <div>
          <div style={footHead}>Contact</div>
          {blocks.footer_email && <div style={{ marginBottom: 6 }}>{blocks.footer_email}</div>}
          {blocks.footer_phone && <div style={{ marginBottom: 6 }}>{blocks.footer_phone}</div>}
          {blocks.footer_address && <div style={{ marginBottom: 6, whiteSpace: 'pre-line' }}>{blocks.footer_address}</div>}
          {socials.length > 0 && (
            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              {socials.map(s => (
                <a key={s.key} href={blocks[s.key]} target="_blank" rel="noopener noreferrer"
                   style={{ color: accent, fontWeight: 700, textDecoration: 'none' }}>
                  {s.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ ...container, marginTop: 30, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
        <span>Powered by <a href="https://app.game-day.app" style={{ color: accent, fontWeight: 800, textDecoration: 'none' }}>Game Day</a> · hockey team & league management</span>
        <a href="/login" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>League member? Sign in →</a>
      </div>
    </footer>
  );
}

const footHead = {
  fontSize: 11, fontWeight: 800, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 10,
};
const footLink = {
  display: 'block', color: 'rgba(255,255,255,0.78)', textDecoration: 'none',
  marginBottom: 7,
};
